// Variance report (Excel) for one schedule, in the client's reconciliation
// format:
//   Book -> Counted (scanned + manual adjustments) -> 1st cut variance
//   -> Reco (entered on the portal, with a reason) -> Recount -> Final diff
// Quantities come from the portal; extra item columns (category, class,
// size, ...) can be filled from the client's book stock / AMD files
// (see reportSources.js). Value columns are at cost (CP) unless named MRP.

import { StyledWorkbook, colName } from '../../reports/engine/styledXlsx';
import { scanStatus, SCAN_STATUS_LABEL } from '../AuditPortal/ComparisonPanel';

export const RECO_REASONS_DEFAULT = [
  'Article Mismatch',
  'Wrong Count by GC',
  'Wrong Scan by SC',
  'Loc Not Scanned',
  'Loc Coding not Done',
  'Manual Adj',
  'Other',
];

// ---- Look -----------------------------------------------------------------

const C = {
  ink: '1F2937',
  muted: '64748B',
  line: 'CBD5E1',
  title: '0F2D52',
  band: {
    item: { head: '334155', soft: 'F1F5F9' },
    rate: { head: '475569', soft: 'F8FAFC' },
    book: { head: '1D4ED8', soft: 'EFF6FF' },
    count: { head: '047857', soft: 'ECFDF5' },
    first: { head: 'B45309', soft: 'FFF7ED' },
    reco: { head: '6D28D9', soft: 'F5F3FF' },
    final: { head: 'B91C1C', soft: 'FEF2F2' },
    value: { head: '0F766E', soft: 'F0FDFA' },
  },
  short: { font: '991B1B', fill: 'FEE2E2' },
  excess: { font: '92400E', fill: 'FEF3C7' },
  match: { font: '166534', fill: 'DCFCE7' },
};

const BAND_LABEL = {
  item: 'ITEM DETAILS',
  rate: 'RATES',
  book: 'BOOK STOCK',
  count: 'PHYSICAL COUNT',
  first: '1ST CUT VARIANCE',
  reco: 'RECONCILIATION',
  final: 'FINAL DIFFERENCE',
  value: 'SHORTAGE / EXCESS',
};

const FMT = {
  money: '#,##0.00',
  moneySigned: '#,##0.00;[Red]-#,##0.00;"–"',
  pct: '0.00%',
  moneyZ: '#,##0.00;-#,##0.00;"–"',
};

const thin = 'thin';
const style = {
  title: { font: { bold: true, size: 16, color: C.title } },
  subtitle: { font: { italic: true, size: 9, color: C.muted } },
  infoLabel: { font: { bold: true, size: 10, color: 'FFFFFF' }, fill: '0F2D52', border: thin, borderColor: '0F2D52', align: { v: 'center', indent: 1 } },
  infoValue: { font: { bold: true, size: 10, color: C.ink }, fill: 'F8FAFC', border: thin, borderColor: C.line, align: { v: 'center', indent: 1 } },
  legendHead: { font: { bold: true, size: 10, color: 'FFFFFF' }, fill: C.band.reco.head, border: thin, borderColor: C.band.reco.head, align: { h: 'center' } },
  legendItem: { font: { size: 10, color: C.ink }, fill: C.band.reco.soft, border: thin, borderColor: 'DDD6FE', align: { indent: 1 } },
  band: (band) => ({ font: { bold: true, size: 9, color: 'FFFFFF' }, fill: C.band[band].head, border: thin, borderColor: C.band[band].head, align: { h: 'center', v: 'center' } }),
  head: (band) => ({ font: { bold: true, size: 10, color: C.ink }, fill: C.band[band].soft, border: { top: thin, bottom: 'medium', left: thin, right: thin }, borderColor: C.band[band].head, align: { h: 'center', v: 'center', wrap: true } }),
  total: (numFmt) => ({ font: { bold: true, size: 10, color: C.ink }, fill: 'E2E8F0', border: { top: 'medium', bottom: 'medium', left: thin, right: thin }, borderColor: '64748B', numFmt, align: numFmt ? undefined : { h: 'left' } }),
  cell: (numFmt, extra = {}) => ({ font: { size: 10, color: C.ink, ...(extra.font || {}) }, border: thin, borderColor: 'E2E8F0', numFmt, ...(extra.fill ? { fill: extra.fill } : {}), ...(extra.align ? { align: extra.align } : {}) }),
  sectionHead: { font: { bold: true, size: 12, color: C.title }, border: { bottom: 'medium' }, borderColor: C.title },
  tableHead: { font: { bold: true, size: 10, color: 'FFFFFF' }, fill: '0F2D52', border: thin, borderColor: '0F2D52', align: { h: 'center', v: 'center', wrap: true } },
  note: { font: { italic: true, size: 9, color: C.muted }, align: { wrap: true, v: 'top' } },
};

const statusLooks = (sheet, sqref) => {
  sheet.addConditional(sqref, { type: 'cellIs', operator: 'equal', formula: '"Short"' }, { font: { bold: true, color: C.short.font }, fill: C.short.fill });
  sheet.addConditional(sqref, { type: 'cellIs', operator: 'equal', formula: '"Excess"' }, { font: { bold: true, color: C.excess.font }, fill: C.excess.fill });
  sheet.addConditional(sqref, { type: 'cellIs', operator: 'equal', formula: '"Match"' }, { font: { color: C.match.font }, fill: C.match.fill });
};

// ---- Numbers ----------------------------------------------------------------

const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const r3 = (v) => Math.round(v * 1000) / 1000;
const r2 = (v) => Math.round(v * 100) / 100;
const times = (qty, rate) => (rate === null ? '' : r2(qty * rate));
const statusOf = (v) => (v === 0 ? 'Match' : v < 0 ? 'Short' : 'Excess');

function dateText(iso, withTime = false) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

/** "1 Nov – 2 Nov 2026" */
export function auditDateRange(audit) {
  const start = audit?.start_at ? new Date(audit.start_at) : null;
  const end = audit?.end_at ? new Date(audit.end_at) : null;
  if (!start || Number.isNaN(start.getTime())) return '';
  const d = (date, year) => date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}) });
  if (!end || Number.isNaN(end.getTime()) || start.toDateString() === end.toDateString()) return d(start, true);
  return `${d(start, start.getFullYear() !== end.getFullYear())} – ${d(end, true)}`;
}

// ---- Items ------------------------------------------------------------------

/**
 * One report row per comparison item, with the reco entry and the extra
 * columns from files merged in. Works out every figure (also written as
 * Excel formulas).
 */
export function buildReportRows({ comparison, reco, extras, costFallback }) {
  const recoByEan = new Map((reco?.entries || []).map((e) => [String(e.ean), e]));
  const categories = (comparison?.adjustment_categories || []).filter((c) =>
    (comparison?.items || []).some((r) => Number(r.adjustments?.[c.code] || 0) !== 0),
  );
  const rows = (comparison?.items || []).map((item) => {
    const extra = extras ? extras(item) : {};
    const pick = (key) => {
      const own = item[key];
      if (own !== null && own !== undefined && String(own).trim() !== '') return own;
      return extra[key] ?? '';
    };
    const mrp = num(item.mrp) ?? num(extra.mrp);
    let cost = num(item.cost) ?? num(extra.cost);
    let costFromMrp = false;
    if (cost === null && mrp !== null && costFallback?.percent > 0) {
      cost = Math.ceil(r2(mrp * (costFallback.percent / 100)));
      costFromMrp = true;
    }
    const adj = {};
    categories.forEach((c) => {
      adj[c.code] = Number(item.adjustments?.[c.code] || 0);
    });
    const counted = r3(
      Number(item.scanned_qty || 0) +
        categories.reduce((t, c) => t + (c.effect === 'add' ? adj[c.code] : c.effect === 'subtract' ? -adj[c.code] : 0), 0),
    );
    const book = Number(item.book_qty || 0);
    const v1 = r3(counted - book);
    const entry = recoByEan.get(String(item.ean));
    const recoQty = entry ? Number(entry.reco_qty || 0) : 0;
    const recount = r3(counted + recoQty);
    const final = r3(recount - book);
    return {
      countedAs: item.by_article ? 'Article' : 'EAN',
      ean: item.ean,
      article: pick('article'),
      description: pick('description'),
      category: pick('category'),
      sub_category: extra.sub_category ?? '',
      class: extra.class ?? '',
      sub_class: extra.sub_class ?? '',
      size: extra.size ?? '',
      color: extra.color ?? '',
      brand: extra.brand ?? '',
      mrp,
      cost,
      costFromMrp,
      book,
      scanned: Number(item.scanned_qty || 0),
      adj,
      counted,
      v1,
      recoQty: entry ? recoQty : null,
      reason: entry?.reason || '',
      remark: entry?.remark || '',
      recoBy: entry?.updated_by_name || '',
      recoAt: entry?.updated_at || '',
      recount,
      final,
      inBook: item.in_book,
    };
  });
  // Items reconciled on the portal that are no longer in the comparison.
  const known = new Set(rows.map((r) => String(r.ean)));
  const orphanReco = (reco?.entries || []).filter((e) => !known.has(String(e.ean)));
  return { rows, categories, orphanReco };
}

/** Column layout of the item sheets. */
function itemColumns(categories, qtyFmt) {
  const qtySigned = `${qtyFmt};[Red]-${qtyFmt};"–"`;
  const cols = [
    { key: 'countedAs', label: 'Counted As', band: 'item', width: 10, text: true },
    { key: 'ean', label: 'EAN', band: 'item', width: 16, text: true },
    { key: 'article', label: 'Article', band: 'item', width: 13, text: true },
    { key: 'description', label: 'Description', band: 'item', width: 38, text: true },
    { key: 'category', label: 'Category', band: 'item', width: 14, text: true },
    { key: 'sub_category', label: 'Sub-Category', band: 'item', width: 14, text: true },
    { key: 'class', label: 'Class', band: 'item', width: 12, text: true },
    { key: 'sub_class', label: 'Sub-class', band: 'item', width: 12, text: true },
    { key: 'size', label: 'Size', band: 'item', width: 8, text: true },
    { key: 'color', label: 'Color', band: 'item', width: 10, text: true },
    { key: 'brand', label: 'Brand', band: 'item', width: 13, text: true },
    { key: 'mrp', label: 'MRP (SP)', band: 'rate', width: 11, fmt: FMT.money },
    { key: 'cost', label: 'Cost (CP)', band: 'rate', width: 11, fmt: FMT.money },
    { key: 'book', label: 'Book Qty', band: 'book', width: 10, fmt: qtyFmt, total: true },
    { key: 'bookVal', label: 'Book Value (CP)', band: 'book', width: 14, fmt: FMT.money, total: true },
    { key: 'scanned', label: 'Scanned Qty', band: 'count', width: 11, fmt: qtyFmt, total: true },
    ...categories.map((c) => ({
      key: `adj:${c.code}`,
      code: c.code,
      effect: c.effect,
      label: `${c.name} ${c.effect === 'add' ? '(+)' : c.effect === 'subtract' ? '(−)' : '(note)'}`,
      band: 'count',
      width: 13,
      fmt: qtyFmt,
      total: true,
    })),
    { key: 'counted', label: 'Counted Qty', band: 'count', width: 11, fmt: qtyFmt, total: true },
    { key: 'countedVal', label: 'Counted Value (CP)', band: 'count', width: 14, fmt: FMT.money, total: true },
    { key: 'v1', label: '1st Cut Variance Qty', band: 'first', width: 12, fmt: qtySigned, total: true },
    { key: 'v1Val', label: '1st Cut Variance Value (CP)', band: 'first', width: 15, fmt: FMT.moneySigned, total: true },
    { key: 'v1Status', label: '1st Cut Status', band: 'first', width: 10, text: true, status: true },
    { key: 'recoQty', label: 'Reco Qty', band: 'reco', width: 10, fmt: qtySigned, total: true },
    { key: 'reason', label: 'Reco Reason', band: 'reco', width: 20, text: true, reason: true },
    { key: 'remark', label: 'Reco Remark', band: 'reco', width: 22, text: true },
    { key: 'recount', label: 'Recount Qty', band: 'final', width: 11, fmt: qtyFmt, total: true },
    { key: 'recountVal', label: 'Recount Value (CP)', band: 'final', width: 14, fmt: FMT.money, total: true },
    { key: 'final', label: 'Final Diff Qty', band: 'final', width: 11, fmt: qtySigned, total: true },
    { key: 'finalVal', label: 'Final Diff Value (CP)', band: 'final', width: 15, fmt: FMT.moneySigned, total: true },
    { key: 'finalStatus', label: 'Final Status', band: 'final', width: 10, text: true, status: true },
    { key: 'shortQty', label: 'Shortage Qty', band: 'value', width: 11, fmt: qtyFmt, total: true },
    { key: 'excessQty', label: 'Excess Qty', band: 'value', width: 11, fmt: qtyFmt, total: true },
    { key: 'shortMrp', zfmt: true, label: 'Shortage MRP', band: 'value', width: 13, fmt: FMT.money, total: true },
    { key: 'excessMrp', zfmt: true, label: 'Excess MRP', band: 'value', width: 13, fmt: FMT.money, total: true },
    { key: 'shortCost', zfmt: true, label: 'Shortage Cost', band: 'value', width: 13, fmt: FMT.money, total: true },
    { key: 'excessCost', zfmt: true, label: 'Excess Cost', band: 'value', width: 13, fmt: FMT.money, total: true },
  ];
  cols.forEach((col, i) => {
    if (col.zfmt) col.fmt = FMT.moneyZ;
    col.index = i + 1;
    col.letter = colName(i + 1);
  });
  return cols;
}

const HEADER_ROW = 9;
const FIRST_DATA_ROW = 10;

/** Header block: schedule details (left) and the reco reason legend (right). */
function writeHeaderBlock(sheet, info, title, legendCol, reasons) {
  sheet.set(1, 1, title, style.title);
  sheet.setRowHeight(1, 24);
  const pairs = [
    ['Schedule Number', info.scheduleNo],
    ['Brand / Client', info.brand],
    ['Store Code', info.storeCode],
    ['Store Name', info.storeName],
    ['Audit Date', info.auditDate],
  ];
  pairs.forEach(([label, value], i) => {
    sheet.merge(2 + i, 1, 2 + i, 2, label, style.infoLabel);
    sheet.merge(2 + i, 3, 2 + i, 4, value || '—', style.infoValue);
  });
  // Report details next to it.
  const right = [
    ['Book stock file', info.bookFile],
    ['Cost basis', info.costBasis],
    ['Extra columns from', info.extraFrom],
    ['Generated', info.generated],
  ];
  right.forEach(([label, value], i) => {
    sheet.merge(2 + i, 6, 2 + i, 7, label, style.infoLabel);
    sheet.merge(2 + i, 8, 2 + i, 13, value || '—', style.infoValue);
  });
  if (legendCol) {
    sheet.merge(1, legendCol, 1, legendCol + 1, 'Reco reasons (Contra SKUs)', style.legendHead);
    const shown = reasons.slice(0, 6);
    shown.forEach((reason, i) => {
      const row = 2 + Math.floor(i / 2);
      const col = legendCol + (i % 2);
      sheet.set(row, col, reason, style.legendItem);
    });
  }
}

/**
 * Writes an item sheet (All items / Differences) with live formulas.
 * Returns { lastRow, cols } for the summary formulas.
 */
function writeItemsSheet(wb, name, rows, ctx) {
  const { categories, info, reasons, qtyFmt, tabColor, title } = ctx;
  const sheet = wb.addSheet(name, { tabColor, landscape: true, fitWidth: true, zoom: 90 });
  const cols = itemColumns(categories, qtyFmt);
  const byKey = Object.fromEntries(cols.map((c) => [c.key, c]));
  const L = (key) => byKey[key].letter;
  cols.forEach((col) => sheet.setColumn(col.index, { width: col.width }));

  writeHeaderBlock(sheet, info, title, byKey.reason.index, reasons);

  // Band row (7): merged group titles.
  let start = 0;
  cols.forEach((col, i) => {
    const next = cols[i + 1];
    if (!next || next.band !== col.band) {
      sheet.merge(7, cols[start].index, 7, col.index, BAND_LABEL[col.band], style.band(col.band));
      start = i + 1;
    }
  });
  sheet.setRowHeight(7, 18);

  // Header row (9).
  cols.forEach((col) => sheet.set(HEADER_ROW, col.index, col.label, style.head(col.band)));
  sheet.setRowHeight(HEADER_ROW, 42);

  const last = FIRST_DATA_ROW + Math.max(rows.length, 1) - 1;
  const rateFmt = { numFmt: FMT.money };

  rows.forEach((row, i) => {
    const r = FIRST_DATA_ROW + i;
    const ref = (key) => `${L(key)}${r}`;
    const zebra = i % 2 === 1 ? { fill: 'FAFBFC' } : {};
    const textStyle = style.cell(undefined, zebra);
    const put = (key, value, fmt, extra) => sheet.set(r, byKey[key].index, value, style.cell(fmt, { ...zebra, ...(extra || {}) }));
    const f = (key, formula, cached, fmt, extra) =>
      sheet.formula(r, byKey[key].index, formula, cached, style.cell(fmt, { ...zebra, ...(extra || {}) }));

    put('countedAs', row.countedAs);
    sheet.set(r, byKey.ean.index, String(row.ean), { ...textStyle, numFmt: '@' });
    ['article', 'description', 'category', 'sub_category', 'class', 'sub_class', 'size', 'color', 'brand'].forEach((key) =>
      sheet.set(r, byKey[key].index, row[key] === '' || row[key] === null ? null : String(row[key]), key === 'article' ? { ...textStyle, numFmt: '@' } : textStyle),
    );
    put('mrp', row.mrp, rateFmt.numFmt);
    if (row.costFromMrp && ctx.costFallback) {
      f('cost', `IF(${ref('mrp')}="","",ROUNDUP(${ref('mrp')}*${ctx.costFallback.percent}%,0))`, row.cost, FMT.money, { font: { italic: true, color: '7C3AED' } });
    } else {
      put('cost', row.cost, FMT.money);
    }
    const M = ref('cost');
    const val = (qtyRef) => `IF(${M}="","",${qtyRef}*${M})`;
    const cachedVal = (qty) => times(qty, row.cost);

    put('book', row.book, qtyFmt);
    f('bookVal', val(ref('book')), cachedVal(row.book), FMT.money);
    put('scanned', row.scanned, qtyFmt);
    const plus = [];
    const minus = [];
    categories.forEach((c) => {
      const key = `adj:${c.code}`;
      put(key, row.adj[c.code] || null, qtyFmt);
      if (c.effect === 'add') plus.push(ref(key));
      if (c.effect === 'subtract') minus.push(ref(key));
    });
    const countedFormula = [ref('scanned'), ...plus.map((x) => `+${x}`), ...minus.map((x) => `-${x}`)].join('');
    f('counted', countedFormula, row.counted, qtyFmt, { font: { bold: true } });
    f('countedVal', val(ref('counted')), cachedVal(row.counted), FMT.money);
    f('v1', `${ref('counted')}-${ref('book')}`, row.v1, `${qtyFmt};[Red]-${qtyFmt};"–"`, { font: { bold: true } });
    f('v1Val', val(ref('v1')), cachedVal(row.v1), FMT.moneySigned);
    f('v1Status', `IF(${ref('v1')}=0,"Match",IF(${ref('v1')}<0,"Short","Excess"))`, statusOf(row.v1), undefined, { align: { h: 'center' } });
    put('recoQty', row.recoQty, `${qtyFmt};[Red]-${qtyFmt};"–"`, { fill: 'F5F3FF' });
    put('reason', row.reason, undefined, { fill: 'F5F3FF' });
    put('remark', row.remark, undefined, { fill: 'F5F3FF' });
    f('recount', `${ref('counted')}+${ref('recoQty')}`, row.recount, qtyFmt);
    f('recountVal', val(ref('recount')), cachedVal(row.recount), FMT.money);
    f('final', `${ref('recount')}-${ref('book')}`, row.final, `${qtyFmt};[Red]-${qtyFmt};"–"`, { font: { bold: true } });
    f('finalVal', val(ref('final')), cachedVal(row.final), FMT.moneySigned, { font: { bold: true } });
    f('finalStatus', `IF(${ref('final')}=0,"Match",IF(${ref('final')}<0,"Short","Excess"))`, statusOf(row.final), undefined, { align: { h: 'center' } });
    const short = Math.max(-row.final, 0);
    const excess = Math.max(row.final, 0);
    const qtyZ = `${qtyFmt};-${qtyFmt};"–"`;
    f('shortQty', `MAX(-${ref('final')},0)`, short, qtyZ);
    f('excessQty', `MAX(${ref('final')},0)`, excess, qtyZ);
    const L_ = ref('mrp');
    f('shortMrp', `IF(${L_}="","",${ref('shortQty')}*${L_})`, times(short, row.mrp), FMT.moneyZ);
    f('excessMrp', `IF(${L_}="","",${ref('excessQty')}*${L_})`, times(excess, row.mrp), FMT.moneyZ);
    f('shortCost', val(ref('shortQty')), cachedVal(short), FMT.moneyZ);
    f('excessCost', val(ref('excessQty')), cachedVal(excess), FMT.moneyZ);
  });

  if (!rows.length) sheet.set(FIRST_DATA_ROW, 1, 'No items.', style.note);

  // Totals row (8): SUBTOTAL so it follows the filter.
  const sumOf = (key) =>
    rows.reduce((t, row) => {
      const v = cachedOf(row, key, categories);
      return t + (typeof v === 'number' ? v : 0);
    }, 0);
  sheet.merge(8, 1, 8, 4, `Total (${rows.length.toLocaleString('en-IN')} items — follows the filter)`, style.total());
  cols.forEach((col) => {
    if (col.index <= 4) return;
    if (col.total) {
      sheet.formula(8, col.index, `SUBTOTAL(9,${col.letter}${FIRST_DATA_ROW}:${col.letter}${last})`, r2(sumOf(col.key)), style.total(col.fmt));
    } else {
      sheet.set(8, col.index, null, style.total());
    }
  });

  sheet.freezeAt(FIRST_DATA_ROW, byKey.description.index);
  sheet.autoFilter(HEADER_ROW, 1, last, cols.length);
  sheet.repeatRows(HEADER_ROW);
  statusLooks(sheet, `${L('v1Status')}${FIRST_DATA_ROW}:${L('v1Status')}${last} ${L('finalStatus')}${FIRST_DATA_ROW}:${L('finalStatus')}${last}`);
  sheet.addList(`${L('reason')}${FIRST_DATA_ROW}:${L('reason')}${last + 200}`, reasons);
  return { sheet, cols, byKey, last };
}

/** Worked-out value of one item column (for totals and the summary). */
function cachedOf(row, key, categories) {
  const short = Math.max(-row.final, 0);
  const excess = Math.max(row.final, 0);
  if (key.startsWith('adj:')) return row.adj[key.slice(4)] || 0;
  switch (key) {
    case 'bookVal': return times(row.book, row.cost);
    case 'countedVal': return times(row.counted, row.cost);
    case 'v1Val': return times(row.v1, row.cost);
    case 'recountVal': return times(row.recount, row.cost);
    case 'finalVal': return times(row.final, row.cost);
    case 'shortQty': return short;
    case 'excessQty': return excess;
    case 'shortMrp': return times(short, row.mrp);
    case 'excessMrp': return times(excess, row.mrp);
    case 'shortCost': return times(short, row.cost);
    case 'excessCost': return times(excess, row.cost);
    case 'recoQty': return row.recoQty || 0;
    default: return row[key];
  }
}

// ---- Summary ----------------------------------------------------------------

function writeSummary(wb, ctx, items) {
  const { info, rows, categories, reasons, qtyFmt, invalid, recoCount } = ctx;
  const sheet = wb.addSheet('Summary', { tabColor: '0F2D52', fitWidth: true, showGrid: false });
  [30, 16, 18, 18, 4, 26, 12, 14, 16].forEach((w, i) => sheet.setColumn(i + 1, { width: w }));
  sheet.set(1, 1, 'Stock Audit — Variance Report', style.title);
  sheet.setRowHeight(1, 26);
  sheet.set(2, 1, `${info.brand || ''}${info.storeName ? ` · ${info.storeName}` : ''}${info.scheduleNo ? ` · ${info.scheduleNo}` : ''}`, style.subtitle);

  const pairs = [
    ['Schedule Number', info.scheduleNo],
    ['Brand / Client', info.brand],
    ['Store Code', info.storeCode],
    ['Store Name', info.storeName],
    ['Audit Date', info.auditDate],
    ['Book stock file', info.bookFile],
    ['Cost basis', info.costBasis],
    ['Generated', info.generated],
  ];
  pairs.forEach(([label, value], i) => {
    sheet.set(4 + i, 1, label, style.infoLabel);
    sheet.merge(4 + i, 2, 4 + i, 4, value || '—', style.infoValue);
  });

  // Stock position (formulas over the All items sheet).
  let r = 4 + pairs.length + 1;
  sheet.merge(r, 1, r, 4, 'Stock position', style.sectionHead);
  r += 1;
  ['', 'Quantity', 'Value at cost (CP)', 'Value at MRP'].forEach((h, i) => sheet.set(r, 1 + i, h || 'Stage', style.tableHead));
  sheet.setRowHeight(r, 20);
  r += 1;
  const { byKey, last } = items;
  const S = "'All items'!";
  const range = (key) => `${S}$${byKey[key].letter}$${FIRST_DATA_ROW}:$${byKey[key].letter}$${last}`;
  const total = (key) => rows.reduce((t, row) => t + (Number(cachedOf(row, key, categories)) || 0), 0);
  const mrpTimes = (qtyKey) => rows.reduce((t, row) => t + (row.mrp === null ? 0 : Number(cachedOf(row, qtyKey, categories)) * row.mrp), 0);
  const sumIf = (key, cond, valueKey) =>
    rows.reduce((t, row) => {
      const v = Number(cachedOf(row, key, categories)) || 0;
      if (!(cond === '<0' ? v < 0 : v > 0)) return t;
      return t + (Number(cachedOf(row, valueKey || key, categories)) || 0);
    }, 0);
  const lineStyle = (fmt, bold) => style.cell(fmt, bold ? { font: { bold: true }, fill: 'F1F5F9' } : {});
  const line = (label, qty, cost, mrp, opts = {}) => {
    sheet.set(r, 1, label, lineStyle(undefined, opts.bold));
    const cell = (col, item, fmt) => {
      if (!item) return sheet.set(r, col, null, lineStyle(fmt, opts.bold));
      return sheet.formula(r, col, item[0], r2(item[1]), lineStyle(fmt, opts.bold));
    };
    cell(2, qty, opts.signed ? `${qtyFmt};[Red]-${qtyFmt};"–"` : qtyFmt);
    cell(3, cost, opts.signed ? FMT.moneySigned : FMT.money);
    cell(4, mrp, opts.signed ? FMT.moneySigned : FMT.money);
    r += 1;
  };
  const sumF = (key) => [`SUM(${range(key)})`, total(key)];
  const sumProdMrp = (key) => [`SUMPRODUCT(${range(key)},${range('mrp')})`, mrpTimes(key)];
  line('Book stock', sumF('book'), sumF('bookVal'), sumProdMrp('book'), { bold: true });
  line('Scanned', sumF('scanned'), null, null);
  categories.forEach((c) => {
    const key = `adj:${c.code}`;
    line(`${c.name} ${c.effect === 'add' ? '(+)' : c.effect === 'subtract' ? '(−)' : '(note, not counted)'}`, sumF(key), null, null);
  });
  line('Counted (scanned + adjustments)', sumF('counted'), sumF('countedVal'), sumProdMrp('counted'), { bold: true });
  line('1st cut shortage', [`-SUMIF(${range('v1')},"<0")`, -sumIf('v1', '<0')], [`-SUMIF(${range('v1')},"<0",${range('v1Val')})`, -sumIf('v1', '<0', 'v1Val')], null);
  line('1st cut excess', [`SUMIF(${range('v1')},">0")`, sumIf('v1', '>0')], [`SUMIF(${range('v1')},">0",${range('v1Val')})`, sumIf('v1', '>0', 'v1Val')], null);
  line('1st cut net variance', sumF('v1'), sumF('v1Val'), null, { bold: true, signed: true });
  line('Reco (net)', sumF('recoQty'), null, null, { signed: true });
  line('Recount', sumF('recount'), sumF('recountVal'), sumProdMrp('recount'), { bold: true });
  line('Final shortage', sumF('shortQty'), sumF('shortCost'), sumF('shortMrp'));
  line('Final excess', sumF('excessQty'), sumF('excessCost'), sumF('excessMrp'));
  line('Final net difference', sumF('final'), sumF('finalVal'), null, { bold: true, signed: true });

  // Accuracy.
  const bookQty = total('book');
  const bookVal = total('bookVal');
  const gross = total('shortQty') + total('excessQty');
  const grossVal = total('shortCost') + total('excessCost');
  sheet.set(r, 1, 'Stock accuracy (1 − (shortage + excess) ÷ book)', lineStyle(undefined, true));
  sheet.formula(r, 2, `IFERROR(1-(SUM(${range('shortQty')})+SUM(${range('excessQty')}))/SUM(${range('book')}),"")`, bookQty ? 1 - gross / bookQty : '', lineStyle(FMT.pct, true));
  sheet.formula(r, 3, `IFERROR(1-(SUM(${range('shortCost')})+SUM(${range('excessCost')}))/SUM(${range('bookVal')}),"")`, bookVal ? 1 - grossVal / bookVal : '', lineStyle(FMT.pct, true));
  sheet.set(r, 4, null, lineStyle(undefined, true));
  r += 2;

  // Item counts (right-hand side, next to the stock position).
  let k = 4 + pairs.length + 1;
  sheet.merge(k, 6, k, 7, 'Items', style.sectionHead);
  k += 1;
  sheet.set(k, 6, 'Count', style.tableHead);
  sheet.set(k, 7, 'Items', style.tableHead);
  k += 1;
  const count = (test) => rows.filter(test).length;
  const counts = [
    ['Items in report', rows.length],
    ['Items in book', count((x) => x.inBook)],
    ['In book, not counted', count((x) => x.inBook && x.counted === 0)],
    ['Counted, not in book', count((x) => !x.inBook)],
    ['Items with 1st cut variance', count((x) => x.v1 !== 0)],
    ['Items reconciled (reco)', recoCount],
    ['Items with final difference', count((x) => x.final !== 0)],
    ['Matched after reco', count((x) => x.final === 0)],
    ['Invalid scans (not counted)', invalid?.scans || 0],
  ];
  counts.forEach(([label, value]) => {
    sheet.set(k, 6, label, style.cell());
    sheet.set(k, 7, value, style.cell('#,##0'));
    k += 1;
  });

  // Reco by reason.
  r = Math.max(r, k + 1);
  sheet.merge(r, 1, r, 4, 'Reconciliation by reason (Contra SKUs)', style.sectionHead);
  r += 1;
  ['Reason', 'Items', 'Reco Qty', 'Reco Value (CP)'].forEach((h, i) => sheet.set(r, 1 + i, h, style.tableHead));
  r += 1;
  const used = new Set(rows.map((x) => x.reason).filter(Boolean));
  const reasonList = [...reasons.filter((x) => used.has(x)), ...[...used].filter((x) => !reasons.includes(x))];
  const reasonRange = range('reason');
  const firstReasonRow = r;
  reasonList.forEach((reason) => {
    const these = rows.filter((x) => x.reason === reason);
    const q = these.reduce((t, x) => t + (x.recoQty || 0), 0);
    const v = these.reduce((t, x) => t + (x.cost === null ? 0 : (x.recoQty || 0) * x.cost), 0);
    const crit = `"${reason.replace(/"/g, '""')}"`;
    sheet.set(r, 1, reason, style.cell());
    sheet.formula(r, 2, `COUNTIF(${reasonRange},${crit})`, these.length, style.cell('#,##0'));
    sheet.formula(r, 3, `SUMIF(${reasonRange},${crit},${range('recoQty')})`, r3(q), style.cell(`${qtyFmt};[Red]-${qtyFmt};"–"`));
    sheet.formula(r, 4, `SUMPRODUCT(--(${reasonRange}=${crit}),${range('recoQty')},${range('cost')})`, r2(v), style.cell(FMT.moneySigned));
    r += 1;
  });
  if (!reasonList.length) {
    sheet.merge(r, 1, r, 4, 'No reco entered on the portal yet.', style.note);
    r += 1;
  } else {
    sheet.set(r, 1, 'Total', style.total());
    sheet.formula(r, 2, `SUM(B${firstReasonRow}:B${r - 1})`, reasonList.reduce((t, x) => t + rows.filter((y) => y.reason === x).length, 0), style.total('#,##0'));
    sheet.formula(r, 3, `SUM(C${firstReasonRow}:C${r - 1})`, r3(rows.reduce((t, x) => t + (x.reason ? x.recoQty || 0 : 0), 0)), style.total(`${qtyFmt};[Red]-${qtyFmt};"–"`));
    sheet.formula(r, 4, `SUM(D${firstReasonRow}:D${r - 1})`, r2(rows.reduce((t, x) => t + (x.reason && x.cost !== null ? (x.recoQty || 0) * x.cost : 0), 0)), style.total(FMT.moneySigned));
    r += 1;
  }

  r += 1;
  const notes = [
    'Counted = Scanned + adjustments marked (+) − adjustments marked (−). Adjustments marked (note) are shown only.',
    '1st cut variance = Counted − Book. Recount = Counted + Reco. Final difference = Recount − Book.',
    'Values are at cost (CP) unless the column says MRP. Values are blank where the rate is missing (never taken as zero).',
    'Reco quantities and reasons are entered on the schedule portal (Reco tab). The All items sheet keeps live formulas: change a Reco Qty there and the totals update.',
  ];
  notes.forEach((text) => {
    sheet.merge(r, 1, r, 9, text, style.note);
    sheet.setRowHeight(r, 26);
    r += 1;
  });
  return sheet;
}

// ---- Reco sheet & scan sheets --------------------------------------------------

function simpleTable(wb, name, { title, subtitle, columns, rows, tabColor }) {
  const sheet = wb.addSheet(name, { tabColor, landscape: true, fitWidth: true });
  columns.forEach((c, i) => sheet.setColumn(i + 1, { width: c.width || 14 }));
  sheet.set(1, 1, title, style.title);
  if (subtitle) sheet.set(2, 1, subtitle, style.subtitle);
  const head = 4;
  columns.forEach((c, i) => sheet.set(head, i + 1, c.label, style.tableHead));
  sheet.setRowHeight(head, 30);
  rows.forEach((row, n) => {
    const zebra = n % 2 === 1 ? { fill: 'FAFBFC' } : {};
    columns.forEach((c, i) => {
      const v = c.value(row);
      const s = style.cell(c.fmt, zebra);
      sheet.set(head + 1 + n, i + 1, v === null || v === undefined ? null : c.fmt ? v : String(v), c.textFmt ? { ...s, numFmt: '@' } : s);
    });
  });
  if (!rows.length) sheet.set(head + 1, 1, 'Nothing to show.', style.note);
  sheet.freezeAt(head + 1, 1);
  sheet.autoFilter(head, 1, head + Math.max(rows.length, 1), columns.length);
  sheet.repeatRows(head);
  return sheet;
}

function scanTime(iso) {
  return dateText(iso, true);
}

// ---- Workbook ---------------------------------------------------------------------

/**
 * data: { audit, comparison, reco, scans }
 * options: { extras (item -> {field: value}), extraFrom (text), costFallback ({percent}|null),
 *            sheets: { differences, reco, scans } }
 */
export async function buildVarianceWorkbook(data, options = {}) {
  const { audit, comparison, reco, scans } = data;
  const reasons = reco?.reasons?.length ? reco.reasons : RECO_REASONS_DEFAULT;
  const { rows, categories, orphanReco } = buildReportRows({
    comparison,
    reco,
    extras: options.extras,
    costFallback: options.costFallback,
  });
  // Report items: book items first, then the rest; each by EAN.
  rows.sort((a, b) => Number(b.inBook) - Number(a.inBook) || String(a.ean).localeCompare(String(b.ean), undefined, { numeric: true }));

  const fractional = rows.some((x) => [x.book, x.scanned, x.counted, x.recoQty || 0].some((v) => !Number.isInteger(v)));
  const qtyFmt = fractional ? '#,##0.000' : '#,##0';
  const costMissing = rows.filter((x) => x.inBook && x.cost === null).length;
  const fromMrp = rows.filter((x) => x.costFromMrp).length;
  const info = {
    scheduleNo: audit?.schedule_no || '',
    brand: audit?.organization_name || '',
    storeCode: audit?.outlet_code || '',
    storeName: audit?.outlet_name || audit?.name || '',
    auditDate: auditDateRange(audit),
    bookFile: comparison?.book?.original_filename
      ? `${comparison.book.original_filename} (${Number(comparison.book.row_count || 0).toLocaleString('en-IN')} rows)`
      : 'No book stock uploaded',
    costBasis: fromMrp
      ? `Book stock CP; ${fromMrp.toLocaleString('en-IN')} item${fromMrp === 1 ? '' : 's'} without CP at ${options.costFallback.percent}% of MRP (rounded up)`
      : costMissing
        ? `Book stock CP (${costMissing.toLocaleString('en-IN')} item${costMissing === 1 ? ' has' : 's have'} no CP)`
        : 'Book stock CP',
    extraFrom: options.extraFrom || 'Portal only',
    generated: new Date().toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
  };
  const ctx = {
    info,
    rows,
    categories,
    reasons,
    qtyFmt,
    costFallback: options.costFallback,
    invalid: comparison?.invalid,
    recoCount: rows.filter((x) => x.recoQty !== null || x.reason).length,
  };

  const wb = new StyledWorkbook();
  // Summary first (it is filled in after All items so it can point at it).
  const summaryIndex = wb.sheets.length;
  const items = writeItemsSheet(wb, 'All items', rows, { ...ctx, tabColor: '1D4ED8', title: 'Variance Report — All items' });
  writeSummary(wb, ctx, items);
  // Move Summary to the front.
  const summary = wb.sheets.pop();
  wb.sheets.splice(summaryIndex, 0, summary);

  const sheets = options.sheets || {};
  if (sheets.differences !== false) {
    const diff = rows.filter((x) => x.v1 !== 0 || x.final !== 0);
    writeItemsSheet(wb, 'Differences', diff, { ...ctx, tabColor: 'B91C1C', title: 'Variance Report — Items with a difference' });
  }
  if (sheets.reco !== false) {
    const recoRows = rows.filter((x) => x.recoQty !== null || x.reason || x.remark);
    const all = [
      ...recoRows,
      ...orphanReco.map((e) => ({
        ean: e.ean, description: '(not in the comparison any more)', book: null, counted: null, v1: null,
        recoQty: Number(e.reco_qty || 0), reason: e.reason || '', remark: e.remark || '', recount: null, final: null,
        recoBy: e.updated_by_name || '', recoAt: e.updated_at, cost: null,
      })),
    ];
    const signed = `${qtyFmt};[Red]-${qtyFmt};"–"`;
    simpleTable(wb, 'Reco', {
      title: 'Reconciliation entries (Contra SKUs)',
      subtitle: 'Entered on the schedule portal, Reco tab.',
      tabColor: '6D28D9',
      rows: all,
      columns: [
        { label: 'EAN', width: 16, value: (x) => x.ean, textFmt: true },
        { label: 'Article', width: 13, value: (x) => x.article || '', textFmt: true },
        { label: 'Description', width: 36, value: (x) => x.description },
        { label: 'Book Qty', width: 10, value: (x) => x.book, fmt: qtyFmt },
        { label: 'Counted Qty', width: 11, value: (x) => x.counted, fmt: qtyFmt },
        { label: '1st Cut Variance', width: 12, value: (x) => x.v1, fmt: signed },
        { label: 'Reco Qty', width: 10, value: (x) => x.recoQty, fmt: signed },
        { label: 'Reco Reason', width: 20, value: (x) => x.reason },
        { label: 'Reco Remark', width: 26, value: (x) => x.remark },
        { label: 'Recount Qty', width: 11, value: (x) => x.recount, fmt: qtyFmt },
        { label: 'Final Diff Qty', width: 11, value: (x) => x.final, fmt: signed },
        { label: 'Final Diff Value (CP)', width: 15, value: (x) => (x.final === null || x.cost === null ? null : r2(x.final * x.cost)), fmt: FMT.moneySigned },
        { label: 'Entered By', width: 18, value: (x) => x.recoBy },
        { label: 'Updated At', width: 20, value: (x) => scanTime(x.recoAt) },
      ],
    });
  }
  if (sheets.scans !== false && scans) {
    const scanRows = scans.rows || [];
    const columns = [
      { label: 'Location', width: 16, value: (x) => x.location, textFmt: true },
      { label: 'EAN', width: 16, value: (x) => x.ean, textFmt: true },
      { label: 'Username', width: 18, value: (x) => x.scanned_by },
      { label: 'Description', width: 34, value: (x) => x.product_name },
      { label: 'Scan Quantity', width: 11, value: (x) => Number(x.qty || 0), fmt: qtyFmt },
      { label: 'Status', width: 16, value: (x) => SCAN_STATUS_LABEL[scanStatus(x)] },
      { label: 'Scanned At', width: 20, value: (x) => scanTime(x.scanned_at) },
      { label: 'Device', width: 16, value: (x) => x.device_id },
      { label: 'Corrected From', width: 16, value: (x) => x.original_ean, textFmt: true },
      { label: 'Resolved By', width: 18, value: (x) => x.corrected_by_name },
      { label: 'Scanned As (Article)', width: 16, value: (x) => x.scanned_code, textFmt: true },
    ];
    simpleTable(wb, 'Valid Scans', { title: 'Valid scans', tabColor: '047857', columns, rows: scanRows.filter((x) => x.is_valid) });
    simpleTable(wb, 'Invalid Resolved', {
      title: 'Invalid scans resolved by the store team',
      subtitle: 'Scanned with an EAN not in the product list, then corrected; counted as valid.',
      tabColor: '0EA5E9',
      columns,
      rows: scanRows.filter((x) => scanStatus(x) === 'resolved'),
    });
    simpleTable(wb, 'Invalid Scans', {
      title: 'Invalid scans (EAN not in the product list — not counted)',
      tabColor: '9CA3AF',
      columns,
      rows: scanRows.filter((x) => !x.is_valid),
    });
  }

  return { bytes: await wb.toBytes(), summary: { items: rows.length, reco: ctx.recoCount, costMissing, fromMrp } };
}

